import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { ListFooter } from '../ui';

// Finance · Manage Banks — own-company bank accounts (RSR + Deroi), extends the
// bank_accounts master built in the PDC module. Admin + accounts only.
// bank_code is UNIQUE and maps a cheque → its owning company (the PDC resolver);
// is_default is the per-company primary; deactivate rather than delete.

const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
const emptyForm = { bank_name: '', bank_code: '', account_number: '', ifsc: '', branch: '', company_id: '', is_default: false };

export default function ManageBanks() {
  const [companies, setCompanies] = useState([]);
  const [banks, setBanks] = useState([]);
  const [modal, setModal] = useState(null);   // form object (has .id when editing)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const loadCompanies = useCallback(() => {
    api.get('/finance/pdc/companies').then(r => setCompanies(r.data.data || [])).catch(() => {});
  }, []);
  const load = useCallback(() => {
    api.get('/finance/pdc/bank-accounts?all=1').then(r => setBanks(r.data.data || [])).catch(() => setBanks([]));
  }, []);
  useEffect(() => { loadCompanies(); load(); }, [loadCompanies, load]);

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      const body = {
        company_id: modal.company_id, bank_code: modal.bank_code, bank_name: modal.bank_name,
        account_number: modal.account_number, ifsc: modal.ifsc, branch: modal.branch, is_default: !!modal.is_default,
      };
      if (!body.company_id) { setErr('Pick a company'); setSaving(false); return; }
      if (!body.bank_code || !body.bank_code.trim()) { setErr('Bank code is required'); setSaving(false); return; }
      if (modal.id) await api.put('/finance/pdc/bank-accounts/' + modal.id, body);
      else await api.post('/finance/pdc/bank-accounts', body);
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (b) => {
    const next = !b.is_active;
    if (!window.confirm(`${next ? 'Reactivate' : 'Deactivate'} bank "${b.bank_code} · ${b.bank_name || ''}"?`)) return;
    try { await api.patch('/finance/pdc/bank-accounts/' + b.id + '/active', { is_active: next }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  // Group by company (code) for display.
  const groups = {};
  banks.forEach(b => { const k = b.company_code || b.company_name || '—'; (groups[k] = groups[k] || []).push(b); });

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };
  const badge = (bg, c, t) => <span style={{ padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: bg, color: c }}>{t}</span>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1 }} />
        <button onClick={() => { setErr(''); setModal({ ...emptyForm }); }}
          style={{ padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
          + Add Bank
        </button>
      </div>

      {/* Helper note */}
      <div style={{ background: '#F7F6F3', border: '1px solid #EDEBE4', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#6a6a6a' }}>
        <b>Bank code</b> maps a cheque/PDC to its owning company — it must be <b>unique</b> and match what's entered on cheques.
        <b> Default</b> is the primary account per company. Deactivate (don't delete) a bank that PDCs may reference.
      </div>

      {Object.keys(groups).length === 0
        ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No banks yet.</div>
        : Object.entries(groups).map(([co, list]) => (
          <div key={co} style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#2D2C72', marginBottom: 8 }}>🏦 {list[0]?.company_name || co}</div>
            <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#F7F6F3' }}>
                  {['Code', 'Bank Name', 'Account No', 'IFSC', 'Branch', 'Flags', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {list.map(b => (
                    <tr key={b.id} style={{ opacity: b.is_active ? 1 : 0.5 }}>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{b.bank_code}</td>
                      <td style={td}>{b.bank_name || '—'}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{b.account_number || '—'}</td>
                      <td style={td}>{b.ifsc || '—'}</td>
                      <td style={td}>{b.branch || '—'}</td>
                      <td style={td}>
                        {b.is_default && badge('#DCFCE7', '#166534', 'Default')}{' '}
                        {b.is_fallback && badge('#EEEDF8', '#3B3A8F', 'Fallback')}
                      </td>
                      <td style={td}>{b.is_active ? badge('#DCFCE7', '#166534', 'Active') : badge('#EEE', '#666', 'Inactive')}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => { setErr(''); setModal({ ...b, is_default: !!b.is_default }); }}
                          style={{ padding: '3px 9px', background: '#EEEDF8', border: 'none', borderRadius: 4, color: '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 4 }}>Edit</button>
                        <button onClick={() => toggleActive(b)}
                          style={{ padding: '3px 9px', background: b.is_active ? '#FCEBEB' : '#DCFCE7', border: 'none', borderRadius: 4, color: b.is_active ? '#E22D2D' : '#166534', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          {b.is_active ? 'Deactivate' : 'Reactivate'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      {Object.keys(groups).length > 0 && <ListFooter shown={banks.length} total={banks.length} noun="banks" />}

      {/* Add / Edit modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, width: 560, maxWidth: '92vw' }}>
            <div style={{ background: '#2D2C72', padding: '14px 22px', borderRadius: '12px 12px 0 0' }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{modal.id ? 'Edit Bank' : 'Add Bank'}</h3>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Bank Name</label><input style={inputStyle} value={modal.bank_name || ''} onChange={e => setModal(m => ({ ...m, bank_name: e.target.value }))} /></div>
                <div>
                  <label style={labelStyle}>Bank Code * (→ company)</label>
                  <input style={inputStyle} value={modal.bank_code || ''} onChange={e => setModal(m => ({ ...m, bank_code: e.target.value }))} placeholder="e.g. 418, 00051" />
                </div>
                <div>
                  <label style={labelStyle}>Company *</label>
                  <select style={inputStyle} value={modal.company_id || ''} onChange={e => setModal(m => ({ ...m, company_id: e.target.value }))}>
                    <option value="">— select —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Account Number</label><input style={inputStyle} value={modal.account_number || ''} onChange={e => setModal(m => ({ ...m, account_number: e.target.value }))} /></div>
                <div><label style={labelStyle}>IFSC</label><input style={inputStyle} value={modal.ifsc || ''} onChange={e => setModal(m => ({ ...m, ifsc: e.target.value }))} /></div>
                <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Branch</label><input style={inputStyle} value={modal.branch || ''} onChange={e => setModal(m => ({ ...m, branch: e.target.value }))} /></div>
                <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" id="isdef" checked={!!modal.is_default} onChange={e => setModal(m => ({ ...m, is_default: e.target.checked }))} />
                  <label htmlFor="isdef" style={{ fontSize: 13, color: '#444' }}>Set as the <b>default</b> account for this company (unsets the current one)</label>
                </div>
              </div>
              {err && <div style={{ marginTop: 12, color: '#A32D2D', fontSize: 13 }}>{err}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
                <button onClick={() => setModal(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button disabled={saving} onClick={save} style={{ padding: '8px 20px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving…' : (modal.id ? 'Update' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
